document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

// Shopify's theme editor dynamically re-renders sections while settings are
// adjusted. Keep editorial reveal sections visible in design mode so moving
// and resizing outfit pieces gives an immediate live preview.
if (window.Shopify?.designMode) {
  document.documentElement.classList.add('shopify-design-mode');
}

document.addEventListener('DOMContentLoaded', () => {
  // Trendsi commonly stores women's shoe sizes as EU values (34–43). Keep
  // those raw option values untouched for Shopify/Trendsi fulfillment, but
  // translate what shoppers see into the US conversion Trendsi publishes.
  const trendsiEuToUsShoeSize = Object.freeze({
    '34': '3',
    '35': '4',
    '36': '5',
    '37': '6',
    '38': '7',
    '39': '8',
    '40': '9',
    '41': '10',
    '42': '10.5',
    '43': '11'
  });

  const parseTrendsiEuShoeSize = (value) => {
    const raw = String(value ?? '').trim();
    const match = raw.match(/^(34|35|36|37|38|39|40|41|42|43)(?:\s*\(\s*US\s*[0-9.]+\s*\))?$/i);
    return match ? match[1] : null;
  };

  const titleLooksLikeShoe = (title = '') => {
    return /\b(shoes?|boots?|sandals?|heels?|pumps?|loafers?|sneakers?|slippers?|mules?|slides?|clogs?|espadrilles?|oxfords?|slingbacks?|footwear|ballet flats?|slip[- ]?ons?)\b|\b(open|round|square|point(?:ed)?)\s+toe\b/i.test(String(title));
  };

  const usesTrendsiShoeSizing = (productTitle, optionName, values = []) => {
    if (!/^size$/i.test(String(optionName).trim()) || !values.length) return false;
    const parsed = values.map(parseTrendsiEuShoeSize);
    if (parsed.some((value) => !value)) return false;

    // An explicit "(US...)" value is definitive. For bare 35/36/etc. values,
    // also require a shoe-like product title so European apparel sizes are not
    // accidentally converted.
    const hasExplicitUsConversion = values.some((value) => /\(\s*US\s*[0-9.]+\s*\)/i.test(String(value)));
    return hasExplicitUsConversion || titleLooksLikeShoe(productTitle);
  };

  const shopperFacingOptionValue = (productTitle, optionName, value, optionValues = []) => {
    if (!usesTrendsiShoeSizing(productTitle, optionName, optionValues)) return String(value ?? '');
    const euSize = parseTrendsiEuShoeSize(value);
    const usSize = euSize ? trendsiEuToUsShoeSize[euSize] : null;
    return usSize ? `US ${usSize} (EU ${euSize})` : String(value ?? '');
  };

  const optionDisplayPriority = (optionName) => {
    const name = String(optionName || '').trim();
    if (/^colou?r$/i.test(name)) return 0;
    if (/^size$/i.test(name)) return 1;
    return 2;
  };

  const btn = document.querySelector('[data-mobile-menu-button]');
  const nav = document.querySelector('[data-mobile-nav]');
  if (btn && nav) {
    btn.addEventListener('click', () => {
      const open = nav.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  document.querySelectorAll('[data-product-select]').forEach((select) => {
    // Product pages use one combined variant dropdown. Rewrite only its visible
    // labels; option values and variant IDs remain exactly as Shopify supplied.
    try {
      const productTitle = select.dataset.productTitle || '';
      const optionNames = JSON.parse(select.dataset.productOptionNames || '[]');
      const variantOptions = Array.from(select.options).map((option) => {
        try { return JSON.parse(option.dataset.variantOptions || '[]'); }
        catch (error) { return []; }
      });
      const valuesByOption = optionNames.map((_, optionIndex) =>
        [...new Set(variantOptions.map((values) => values?.[optionIndex]).filter(Boolean))]
      );

      Array.from(select.options).forEach((option, variantIndex) => {
        const values = variantOptions[variantIndex];
        if (!values?.length) return;
        const displayValues = values.map((value, optionIndex) =>
          shopperFacingOptionValue(productTitle, optionNames[optionIndex], value, valuesByOption[optionIndex])
        );
        option.textContent = `${displayValues.join(' / ')}${option.dataset.available === 'true' ? '' : ' — Sold out'}`;
      });
    } catch (error) {
      console.debug('Product size display conversion skipped', error);
    }

    select.addEventListener('change', () => {
      const option = select.options[select.selectedIndex];
      const price = option.dataset.price;
      const available = option.dataset.available === 'true';
      const scope = select.closest('[data-product-form]');
      const priceEl = scope?.querySelector('[data-product-price]');
      const button = scope?.querySelector('button[type=submit]');
      if (priceEl && price) priceEl.textContent = price;
      if (button) {
        button.disabled = !available;
        button.textContent = available ? 'Add to bag' : 'Sold out';
      }
    });
  });

  // Subtle reveal motion for the editorial homepage. Content remains visible
  // without JS and respects prefers-reduced-motion in CSS.
  const revealItems = document.querySelectorAll('[data-reveal]');
  if (window.Shopify?.designMode) {
    revealItems.forEach((item) => item.classList.add('is-visible'));
  } else if ('IntersectionObserver' in window && revealItems.length) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    revealItems.forEach((item) => observer.observe(item));
  } else {
    revealItems.forEach((item) => item.classList.add('is-visible'));
  }

  // Sections/blocks are swapped into the preview without a full page load.
  // Make newly-rendered editorial sections visible immediately while editing.
  if (window.Shopify?.designMode) {
    const showEditorSection = (root) => {
      if (!root) return;
      if (root.matches?.('[data-reveal]')) root.classList.add('is-visible');
      root.querySelectorAll?.('[data-reveal]').forEach((item) => item.classList.add('is-visible'));
    };
    document.addEventListener('shopify:section:load', (event) => showEditorSection(event.target));
    document.addEventListener('shopify:block:select', (event) => showEditorSection(event.target));
  }

  // Pretty Intentions "Get the Look" drawer. This works for the magazine
  // cover AND every shoppable outfit spread, and lets the shopper choose the
  // exact available color / size / variant before the look is added to bag.
  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const drawer = document.createElement('div');
  drawer.className = 'look-drawer';
  drawer.hidden = true;
  drawer.innerHTML = `
    <div class="look-drawer__backdrop" data-look-drawer-close></div>
    <aside class="look-drawer__panel" role="dialog" tabindex="-1" aria-modal="true" aria-labelledby="look-drawer-title">
      <button class="look-drawer__close" type="button" aria-label="Close look options" data-look-drawer-close>×</button>
      <div class="look-drawer__header">
        <div class="eyebrow">Complete the look</div>
        <h2 id="look-drawer-title" data-look-drawer-title>Choose your options</h2>
        <p>Pick your preferred color and size for each piece, then add the finished look to your bag.</p>
      </div>
      <div class="look-drawer__items" data-look-drawer-items></div>
      <div class="look-drawer__footer">
        <div class="look-drawer__footer-copy">
          <span data-look-drawer-count></span>
          <span data-look-drawer-status aria-live="polite"></span>
        </div>
        <button class="button look-drawer__add" type="button" data-look-drawer-add>Add selected look to bag</button>
      </div>
    </aside>
  `;
  document.body.appendChild(drawer);

  const drawerPanel = drawer.querySelector('.look-drawer__panel');
  const drawerTitle = drawer.querySelector('[data-look-drawer-title]');
  const drawerItems = drawer.querySelector('[data-look-drawer-items]');
  const drawerCount = drawer.querySelector('[data-look-drawer-count]');
  const drawerStatus = drawer.querySelector('[data-look-drawer-status]');
  const drawerAdd = drawer.querySelector('[data-look-drawer-add]');
  let drawerTrigger = null;
  let activeSelections = [];

  const getLookProducts = (section) => {
    const seen = new Set();
    const products = [];
    section.querySelectorAll('[data-look-product-json]').forEach((script) => {
      try {
        const product = JSON.parse(script.textContent);
        if (!product?.id || seen.has(product.id)) return;
        seen.add(product.id);
        products.push(product);
      } catch (error) {
        console.error('Unable to read look product data', error);
      }
    });
    return products;
  };

  const uniqueOptionValues = (product, index) => {
    return [...new Set(product.variants.map((variant) => variant.options?.[index]).filter(Boolean))];
  };

  const isDefaultOnlyProduct = (product) => {
    return product.optionNames?.length === 1
      && product.optionNames[0] === 'Title'
      && product.variants.every((variant) => variant.options?.[0] === 'Default Title');
  };

  // Prefer the variant that matches the editorial "Shown in" value when one
  // is configured. This keeps the drawer aligned with the outfit photo instead
  // of silently starting on the first available color.
  const preferredAvailableVariant = (product) => {
    const shownIn = String(product.shownIn || '').trim().toLowerCase();
    if (shownIn) {
      const shownVariant = product.variants.find((variant) =>
        variant.available && variant.options?.some((value) =>
          String(value).trim().toLowerCase() === shownIn
        )
      );
      if (shownVariant) return shownVariant;
    }
    return product.variants.find((variant) => variant.available) || null;
  };

  const updateDrawerAddState = () => {
    const invalid = activeSelections.some((selection) => !selection.variant?.available);
    drawerAdd.disabled = invalid || !activeSelections.length;
    if (invalid) {
      drawerStatus.textContent = 'Choose an available option for each piece.';
    } else {
      drawerStatus.textContent = '';
    }
  };

  const renderLookProduct = (product) => {
    const initialVariant = preferredAvailableVariant(product) || product.variants[0] || null;
    const selection = { product, variant: initialVariant, selects: [] };
    activeSelections.push(selection);

    const item = document.createElement('article');
    item.className = 'look-drawer__item';
    item.dataset.productId = product.id;

    const mediaHtml = product.image
      ? `<a class="look-drawer__media" href="${escapeHtml(product.url)}"><img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}"></a>`
      : `<a class="look-drawer__media look-drawer__media--empty" href="${escapeHtml(product.url)}" aria-label="View ${escapeHtml(product.title)}"><span>View piece</span></a>`;

    item.innerHTML = `
      ${mediaHtml}
      <div class="look-drawer__item-main">
        <div class="look-drawer__item-topline">
          <a class="look-drawer__product-title" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
          <span class="look-drawer__price" data-look-price>${escapeHtml(initialVariant?.price || '')}</span>
        </div>
        <div class="look-drawer__options" data-look-options></div>
        <div class="look-drawer__availability" data-look-availability></div>
      </div>
    `;

    const optionsWrap = item.querySelector('[data-look-options]');
    const availability = item.querySelector('[data-look-availability]');
    const price = item.querySelector('[data-look-price]');

    if (isDefaultOnlyProduct(product)) {
      optionsWrap.innerHTML = '<span class="look-drawer__ready">Ready to add</span>';
    } else {
      const shownIn = String(product.shownIn || '').trim();

      // Keep the visual order consistent across products: Color on the left,
      // Size on the right. The selects are still stored by Shopify's original
      // option index, so variant matching is unaffected even when a supplier
      // created Size before Color (the tights are one example).
      const optionIndexes = product.optionNames
        .map((_, index) => index)
        .sort((a, b) => optionDisplayPriority(product.optionNames[a]) - optionDisplayPriority(product.optionNames[b]) || a - b);

      optionIndexes.forEach((optionIndex) => {
        const optionName = product.optionNames[optionIndex];
        const optionValues = uniqueOptionValues(product, optionIndex);
        const field = document.createElement('label');
        field.className = 'look-drawer__field';
        const labelText = document.createElement('span');
        labelText.textContent = optionName;
        const select = document.createElement('select');
        select.dataset.optionIndex = optionIndex;

        optionValues.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = shopperFacingOptionValue(product.title, optionName, value, optionValues);
          select.appendChild(option);
        });

        if (initialVariant?.options?.[optionIndex]) select.value = initialVariant.options[optionIndex];
        field.append(labelText, select);

        // A single Material value is not a real shopper choice, so keep it
        // selected internally for variant matching without showing a dropdown.
        const hideSingleMaterial = /^material$/i.test(String(optionName).trim()) && optionValues.length <= 1;
        if (!hideSingleMaterial) {
          optionsWrap.appendChild(field);
        }

        // Keep every option in this array, including hidden single-value Material,
        // at its ORIGINAL Shopify index so raw values still resolve the correct
        // Trendsi variant when the look is added to the bag.
        selection.selects[optionIndex] = select;
      });
    }

    const refreshSelection = () => {
      if (!selection.selects.length) {
        selection.variant = initialVariant?.available ? initialVariant : null;
      } else {
        const selectedValues = selection.selects.map((select) => select.value);
        const match = product.variants.find((variant) =>
          variant.options?.every((value, index) => value === selectedValues[index])
        );
        selection.variant = match?.available ? match : null;

        // Only disable values that are completely unavailable for this product.
        // Do not lock a shopper out of a color just because their current size is
        // unavailable in that color; they can still change the other selector.
        selection.selects.forEach((select, optionIndex) => {
          Array.from(select.options).forEach((option) => {
            const possible = product.variants.some((variant) =>
              variant.available && variant.options?.[optionIndex] === option.value
            );
            option.disabled = !possible;
          });
        });
      }

      const exactVariant = selection.variant;
      if (exactVariant) {
        availability.innerHTML = product.shownIn
          ? `<span class="look-drawer__availability-label">AVAILABLE</span><span class="look-drawer__shown-status">Shown in: <strong>${escapeHtml(product.shownIn)}</strong></span>`
          : `<span class="look-drawer__availability-label">AVAILABLE</span>`;
        item.classList.remove('is-unavailable');
        price.textContent = exactVariant.price || '';
      } else {
        availability.innerHTML = product.shownIn
          ? `<span class="look-drawer__availability-label">That combination is sold out</span><span class="look-drawer__shown-status">Shown in: <strong>${escapeHtml(product.shownIn)}</strong></span>`
          : `<span class="look-drawer__availability-label">That combination is sold out</span>`;
        item.classList.add('is-unavailable');
      }
      updateDrawerAddState();
    };

    selection.selects.forEach((select) => select.addEventListener('change', refreshSelection));
    refreshSelection();
    return item;
  };

  const openLookDrawer = (section, trigger) => {
    const products = getLookProducts(section);
    if (!products.length) {
      if (!trigger.dataset.originalLabel) trigger.dataset.originalLabel = trigger.textContent;
      trigger.textContent = 'Choose products first';
      window.setTimeout(() => { trigger.textContent = trigger.dataset.originalLabel; }, 1800);
      return;
    }

    drawerTrigger = trigger;
    activeSelections = [];
    drawerItems.innerHTML = '';
    drawerStatus.textContent = '';
    drawerAdd.textContent = 'Add selected look to bag';
    drawerTitle.textContent = section.dataset.lookTitle || 'Choose your options';
    drawerCount.textContent = `${products.length} ${products.length === 1 ? 'piece' : 'pieces'} in this look`;
    products.forEach((product) => drawerItems.appendChild(renderLookProduct(product)));

    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    document.body.classList.add('look-drawer-open');
    window.setTimeout(() => drawerPanel?.focus?.(), 50);
  };

  const closeLookDrawer = () => {
    drawer.classList.remove('is-open');
    document.body.classList.remove('look-drawer-open');
    window.setTimeout(() => { drawer.hidden = true; }, 260);
    drawerTrigger?.focus?.();
  };

  drawer.querySelectorAll('[data-look-drawer-close]').forEach((control) => {
    control.addEventListener('click', closeLookDrawer);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !drawer.hidden) closeLookDrawer();
  });

  // Delegate this click so Get the Look keeps working when Shopify's theme
  // editor reloads or re-renders a section without refreshing the whole page.
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-shop-look]');
    if (!button) return;
    const section = button.closest('[data-look-section]');
    if (!section) return;
    event.preventDefault();
    openLookDrawer(section, button);
  });

  drawerAdd.addEventListener('click', async () => {
    const items = activeSelections
      .filter((selection) => selection.variant?.available)
      // Shopify accepts variant IDs as strings. Keeping them as strings avoids
      // unnecessary numeric conversion and is safer for long platform IDs.
      .map((selection) => ({ id: String(selection.variant.id), quantity: 1 }));

    if (!items.length || items.length !== activeSelections.length) {
      updateDrawerAddState();
      return;
    }

    drawerAdd.disabled = true;
    drawerAdd.setAttribute('aria-busy', 'true');
    drawerAdd.textContent = 'Adding your look…';
    drawerStatus.textContent = '';

    try {
      const root = window.Shopify?.routes?.root || '/';
      // Shopify's official Ajax Cart API supports adding multiple variants in
      // one request. The locale-aware routes root keeps this valid for markets.
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.description || payload.message || 'Unable to add this look right now.');
      }

      drawerAdd.removeAttribute('aria-busy');
      drawerAdd.textContent = 'Added to bag ♡';
      drawerStatus.textContent = 'Your selected sizes and colors are in the bag.';

      // Close the look drawer automatically after a successful add. Give the
      // shopper a brief confirmation first so the action still feels clear.
      window.setTimeout(() => closeLookDrawer(), 450);

      // Keep the shopper on the magazine page, but refresh the visible bag count.
      try {
        const cartResponse = await fetch(`${root}cart.js`, { headers: { 'Accept': 'application/json' } });
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          document.querySelectorAll('.header-actions a[href*="/cart"]').forEach((link) => {
            link.textContent = `Bag (${cart.item_count})`;
          });
        }
      } catch (cartError) {
        console.debug('Cart count refresh skipped', cartError);
      }
    } catch (error) {
      console.error(error);
      drawerAdd.removeAttribute('aria-busy');
      drawerAdd.disabled = false;
      drawerAdd.textContent = 'Try again';
      drawerStatus.textContent = error?.message || 'We could not add the look. Please try again.';
      window.setTimeout(() => {
        if (!drawer.hidden) drawerAdd.textContent = 'Add selected look to bag';
      }, 1800);
    }
  });
});
